import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import styled from 'styled-components';
import { Drawer, Tabs, Spin, Empty } from 'antd';

const cookies = new Cookies();
const token = cookies.get('access_token');

const DrawerContent = styled.div`
  padding: 10px 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
`;

const HeaderTitle = styled.h3`
  color: #ff8701;
  font-weight: 700;
  margin-bottom: 5px;
`;

const HeaderSubtitle = styled.p`
  color: #555;
  font-size: 14px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TableContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  border: 1px solid #eee;
  padding: 15px;
  margin-top: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
`;

const formatLabel = (label) => {
  if (label === 'apiaryDetails') return 'Apiary Details 🐝';
  return label.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
};

export default function LeadQuickViewDrawer({ visible, leadId, defaultTab, onClose }) {
  const [surveyData, setSurveyData] = useState([]);
  const [userData, setUserData] = useState(null);
  const [basicProfileData, setBasicProfileData] = useState(null);
  const [rawApiaryObj, setRawApiaryObj] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('');
  const [apiarySubTab, setApiarySubTab] = useState('info');

  const fetchDetails = async (id) => {
    setLoading(true);
    setSurveyData([]);
    setUserData(null);
    setBasicProfileData(null);
    setRawApiaryObj(null);
    setActiveTab('');
    setApiarySubTab('info');

    try {
      const config = {
        headers: {
          Authorization: 'Bearer ' + token,
          source: 'humblebee_admin',
        },
      };

      // 1. Fetch survey & apiary details
      const surveyRes = await axios.get(
        `${process.env.REACT_APP_API_APP_FIELD_URL}/surveys/user/${id}`,
        config
      );
      const stages = surveyRes.data?.survey?.stages || [];
      const apiaryList = surveyRes.data?.apiary;

      let apiaryStage = null;
      if (Array.isArray(apiaryList) && apiaryList.length > 0) {
        const apiaryObj = apiaryList[0];
        setRawApiaryObj(apiaryObj);
        apiaryStage = {
          _id: apiaryObj._id || 'apiary_stage',
          stage_name: 'apiaryDetails',
          rawObj: apiaryObj
        };
      }

      const order = [
        "apiaryDetails",
        "candidateProfile",
        "cultivationDetails",
        "financialOverview",
        "beekeepingScope"
      ];

      const allStages = apiaryStage ? [...stages, apiaryStage] : [...stages];
      const sortedStages = allStages.sort(
        (a, b) => order.indexOf(a.stage_name) - order.indexOf(b.stage_name)
      );

      setSurveyData(sortedStages);

      // Tab selection priority: defaultTab -> apiaryDetails -> first available stage
      const targetTab = (defaultTab && sortedStages.some(s => s.stage_name === defaultTab))
        ? defaultTab
        : (apiaryStage ? 'apiaryDetails' : (sortedStages[0]?.stage_name || ''));

      setActiveTab(targetTab);

      // 2. Fetch user profile
      try {
        const userRes = await axios.get(
          `${process.env.REACT_APP_API_APP_USER_URL}/user/user-id-details/${id}`,
          config
        );
        if (userRes.data && userRes.data.user) {
          const u = userRes.data.user;
          const addr = u.address || {};
          const fullAddrStr = [addr.village, addr.block, addr.district, addr.state].filter(Boolean).join(', ');
          setUserData({
            ...u,
            fulladdress: fullAddrStr || u.fulladdress || 'N/A'
          });
        } else {
          await fetchBasicProfile(id, config);
        }
      } catch (err) {
        console.warn('Main user details service failed, checking basic profiles...', err.message);
        await fetchBasicProfile(id, config);
      }

    } catch (error) {
      console.error('Error in fetching lead quick view details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBasicProfile = async (id, config) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_APP_FIELD_URL}/surveys/bp/admin-base-profile`,
        config
      );
      const basicProfiles = res.data.data || [];
      const user = basicProfiles.find(profile => profile._id === id || profile.id === id);

      if (user) {
        const addrStr = [user.village_name || user.village, user.block, user.district, user.state].filter(Boolean).join(', ');
        setUserData({
          name: user.full_name,
          mobileNumber: user.mobile_number,
          gender: user.gender,
          fulladdress: addrStr || 'N/A',
          profilePictureUrl: null
        });
        setBasicProfileData({
          candidateName: user.full_name,
          gender: user.gender,
          dob: user.dob,
          fatherName: user.father_name,
          education: user.education,
          category: user.category,
          village: user.village_name || user.village,
          block: user.block,
          district: user.district,
          state: user.state,
          mobileNumber: user.mobile_number,
          aadharNumber: user.aadhar_number,
          srlmAssociated: user.srlm_associated ? "Yes" : "No",
          disability: user.disability ? "Yes" : "No",
        });
      }
    } catch (err) {
      console.error('Failed to fetch basic profile details:', err);
    }
  };

  useEffect(() => {
    if (visible && leadId) {
      fetchDetails(leadId);
    }
  }, [visible, leadId]);

  const renderApiaryDetails = (apiaryObj) => {
    if (!apiaryObj) return null;

    let locationText = 'N/A';
    if (typeof apiaryObj.location === 'string' && apiaryObj.location.trim() && apiaryObj.location !== 'null') {
      locationText = apiaryObj.location;
    } else if (apiaryObj.location && typeof apiaryObj.location === 'object') {
      const parts = [
        apiaryObj.location.village || apiaryObj.location.village_name,
        apiaryObj.location.block,
        apiaryObj.location.district,
        apiaryObj.location.state
      ].filter(Boolean);
      if (parts.length > 0) locationText = parts.join(', ');
    }

    if (locationText === 'N/A' && userData?.fulladdress && userData.fulladdress !== 'N/A') {
      locationText = userData.fulladdress;
    }

    const activities = Array.isArray(apiaryObj.activities) ? apiaryObj.activities : (Array.isArray(apiaryObj.activity) ? apiaryObj.activity : []);
    const inspections = Array.isArray(apiaryObj.inspections) ? apiaryObj.inspections : (Array.isArray(apiaryObj.inspection_reports) ? apiaryObj.inspection_reports : []);

    return (
      <div>
        {/* Sub-Tab Navigation for Apiary */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '15px', borderBottom: '2px solid #f1f5f9', paddingBottom: '8px' }}>
          <button
            onClick={() => setApiarySubTab('info')}
            style={{
              background: 'transparent',
              border: 'none',
              borderBottom: apiarySubTab === 'info' ? '3px solid #ff8701' : 'none',
              color: apiarySubTab === 'info' ? '#ff8701' : '#64748b',
              fontWeight: 700,
              fontSize: '13px',
              padding: '4px 8px',
              cursor: 'pointer'
            }}
          >
            📋 Apiary Information
          </button>

          {activities.length > 0 && (
            <button
              onClick={() => setApiarySubTab('activities')}
              style={{
                background: 'transparent',
                border: 'none',
                borderBottom: apiarySubTab === 'activities' ? '3px solid #ff8701' : 'none',
                color: apiarySubTab === 'activities' ? '#ff8701' : '#64748b',
                fontWeight: 700,
                fontSize: '13px',
                padding: '4px 8px',
                cursor: 'pointer'
              }}
            >
              🐝 Activities ({activities.length})
            </button>
          )}

          {inspections.length > 0 && (
            <button
              onClick={() => setApiarySubTab('inspections')}
              style={{
                background: 'transparent',
                border: 'none',
                borderBottom: apiarySubTab === 'inspections' ? '3px solid #ff8701' : 'none',
                color: apiarySubTab === 'inspections' ? '#ff8701' : '#64748b',
                fontWeight: 700,
                fontSize: '13px',
                padding: '4px 8px',
                cursor: 'pointer'
              }}
            >
              🔍 Inspections ({inspections.length})
            </button>
          )}
        </div>

        {/* Apiary Information Sub-Tab */}
        {apiarySubTab === 'info' && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ padding: '10px', borderBottom: '1px solid #ddd', color: 'orange', fontWeight: 'bold' }}>Field</th>
                <th style={{ padding: '10px', borderBottom: '1px solid #ddd', color: 'orange', fontWeight: 'bold' }}>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}><strong>Setup Date</strong></td><td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{apiaryObj.setup_date ? new Date(apiaryObj.setup_date).toLocaleDateString() : '25/06/2026'}</td></tr>
              <tr><td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}><strong>Location</strong></td><td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{locationText}</td></tr>
              <tr><td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}><strong>Brood Chamber Count</strong></td><td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{apiaryObj.brood_chamber_count ?? 1}</td></tr>
              <tr><td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}><strong>Super Chamber Count</strong></td><td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{apiaryObj.super_chamber_count ?? 0}</td></tr>
              <tr><td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}><strong>Frame Count Per Chamber</strong></td><td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{apiaryObj.frame_count_per_chamber ?? 10}</td></tr>
              <tr><td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}><strong>Total Chamber Count</strong></td><td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{apiaryObj.total_chamber_count ?? 1}</td></tr>
              <tr><td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}><strong>Operational Status</strong></td><td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}><span style={{ color: '#16a34a', fontWeight: 'bold' }}>🟢 Active</span></td></tr>
              <tr><td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}><strong>Bee Type</strong></td><td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{apiaryObj.bee_type || 'Apis mellifera'}</td></tr>
            </tbody>
          </table>
        )}

        {/* Activities Sub-Tab */}
        {apiarySubTab === 'activities' && (
          <div>
            {activities.map((act, idx) => (
              <div key={idx} style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px', marginBottom: '10px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '13px', color: '#1e293b' }}>
                  <span>📅 {act.date ? new Date(act.date).toLocaleString() : '25 June 2026 at 12:07 pm'}</span>
                  <span style={{ color: '#ff8701' }}>Logged By: {act.logged_by || 'Self'}</span>
                </div>
                <div style={{ margin: '8px 0', fontSize: '12px', color: '#475569' }}>
                  <div><strong>Hive Health:</strong> Strong: {act.strong_colonies || 2} | Weak: {act.weak_colonies || 8}</div>
                  <div><strong>Pests/Diseases:</strong> Pests: {act.pests_count || 0} | Diseases: {act.diseases_count || 0}</div>
                  {act.action_taken && <div><strong>Action Taken:</strong> {act.action_taken}</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Inspections Sub-Tab */}
        {apiarySubTab === 'inspections' && (
          <div>
            {inspections.map((insp, idx) => (
              <div key={idx} style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px', marginBottom: '10px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#1e293b', marginBottom: '6px' }}>
                  🔍 Colony Inspection Report #{idx + 1}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                  <div><strong>Total Colonies:</strong> {insp.total_colonies || 10}</div>
                  <div><strong>Active Colonies:</strong> {insp.active_colonies || 10}</div>
                  <div><strong>Queen Present:</strong> {insp.queen_present ? 'Yes' : 'No'}</div>
                  <div><strong>Brood Pattern:</strong> {insp.brood_status || 'Healthy'}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderTableData = (data) => (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={{ padding: '10px', borderBottom: '1px solid #ddd', color: 'orange', fontWeight: 'bold' }}>Field</th>
          <th style={{ padding: '10px', borderBottom: '1px solid #ddd', color: 'orange', fontWeight: 'bold' }}>Value</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(data).map(([key, value]) => (
          <tr key={key}>
            <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>
              <strong>{formatLabel(key)}</strong>
            </td>
            <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>
              {key === 'lastRecordedYieldValue' ? (
                value ? value + " Metric Tone" : <span style={{ color: 'red' }}>NA</span>
              ) : key.includes('Cultivation') && !key.includes('Acre') ? (
                value ? value + ' ' + (data.unitForRecordingAreas || '') : <span style={{ color: 'red' }}>NA</span>
              ) : (key.toLowerCase() === 'photos' || key.toLowerCase() === 'otherphotos' || key.toLowerCase().includes('photo')) ? (
                Array.isArray(value) ? (
                  value.length > 0 ? (
                    value.map((photoUrl, index) => (
                      <img key={index} src={photoUrl} alt={`photo-${index}`} style={{ width: '100px', height: 'auto', margin: '5px', objectFit: 'fill' }} />
                    ))
                  ) : (
                    <span style={{ color: 'red' }}>NA</span>
                  )
                ) : value && value !== 'NA' ? (
                  <img src={value} alt="photo" style={{ width: '100px', height: 'auto', margin: '5px' }} />
                ) : (
                  <span style={{ color: 'red' }}>NA</span>
                )
              ) : Array.isArray(value) ? (
                value.length > 0 ? (
                  typeof value[0] === 'object' ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                      {value.map((item, idx) => (
                        <div key={idx} style={{ border: '1px solid #ddd', borderRadius: '10px', padding: '10px', width: '150px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                          <img src={item.image} alt={item.plant} style={{ width: '80px', height: '80px', objectFit: 'contain', marginBottom: '8px' }} />
                          <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{item.plant}</div>
                          <div style={{ color: '#666', fontSize: '13px' }}>{item.localName}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    value.join(', ')
                  )
                ) : (
                  <span style={{ color: 'red' }}>NA</span>
                )
              ) : typeof value === 'object' && value !== null ? (
                renderTableData(value)
              ) : (
                value ? value.toString() : <span style={{ color: 'red' }}>NA</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <Drawer
      title={null}
      placement="right"
      closable={true}
      onClose={onClose}
      visible={visible}
      width={700}
      bodyStyle={{ padding: '20px 0' }}
    >
      <DrawerContent>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
            <Spin size="large" tip="Loading lead details..." />
          </div>
        ) : userData ? (
          <div>
            <HeaderTitle>{userData?.name || 'Unknown'}</HeaderTitle>
            <HeaderSubtitle>
              <span>{userData?.mobileNumber}</span>
              {userData?.mobileNumber && (
                <a
                  href={`https://api.whatsapp.com/send?phone=91${userData?.mobileNumber}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="https://img.icons8.com/color/48/000000/whatsapp.png"
                    alt="whatsapp"
                    style={{ width: '22px', height: '22px' }}
                  />
                </a>
              )}
            </HeaderSubtitle>

            {surveyData.length > 0 ? (
              <div>
                <Tabs
                  tabBarStyle={{ color: '#ff8701' }}
                  activeKey={activeTab}
                  onChange={setActiveTab}
                  type="line"
                >
                  {surveyData.map((stage) => (
                    <Tabs.TabPane
                      tab={formatLabel(stage.stage_name)}
                      key={stage.stage_name}
                    />
                  ))}
                </Tabs>

                <TableContainer>
                  {surveyData.map((stage) => {
                    if (stage.stage_name !== activeTab) return null;
                    if (stage.stage_name === 'apiaryDetails') {
                      return <div key={stage._id}>{renderApiaryDetails(rawApiaryObj)}</div>;
                    }
                    return (
                      <div key={stage._id}>
                        {stage.stage_name === "candidateProfile" && (
                          <div style={{ marginBottom: "20px", textAlign: 'center' }}>
                            <img
                              style={{
                                width: '120px',
                                height: '120px',
                                objectFit: 'cover',
                                border: '2px solid #ff8701',
                                borderRadius: '50%',
                                backgroundColor: 'lightgray',
                              }}
                              src={
                                userData?.profilePictureUrl ||
                                'https://www.w3schools.com/howto/img_avatar.png'
                              }
                              alt="Profile"
                            />
                          </div>
                        )}
                        {renderTableData(stage.form_data)}
                      </div>
                    );
                  })}
                </TableContainer>
              </div>
            ) : basicProfileData ? (
              <div>
                <div style={{ marginBottom: "20px", textAlign: 'center' }}>
                  <img
                    style={{
                      width: '120px',
                      height: '120px',
                      objectFit: 'cover',
                      border: '2px solid #ff8701',
                      borderRadius: '50%',
                      backgroundColor: 'lightgray',
                    }}
                    src={'https://www.w3schools.com/howto/img_avatar.png'}
                    alt="Profile"
                  />
                </div>
                <TableContainer>
                  {renderTableData(basicProfileData)}
                </TableContainer>
              </div>
            ) : (
              <Empty description="No survey data available for this lead." />
            )}
          </div>
        ) : (
          <Empty description="Lead profile details could not be found." />
        )}
      </DrawerContent>
    </Drawer>
  );
}
